import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Loader2, Search, Send, Star, TrendingDown, RefreshCw, ExternalLink, CheckCircle2, XCircle } from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';
import PartnerLinksTab from './PartnerLinksTab';

const eur = (v) => (v === null || v === undefined) ? '—' : Number(v).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
const STATUS_META = {
  sent: { label: 'Inviato', cls: 'bg-blue-100 text-blue-700' },
  viewed: { label: 'Visto', cls: 'bg-indigo-100 text-indigo-700' },
  requested: { label: 'Vuole prenotare', cls: 'bg-amber-100 text-amber-700' },
  booked: { label: 'Prenotato', cls: 'bg-emerald-100 text-emerald-700' },
  lost: { label: 'Perso', cls: 'bg-slate-100 text-slate-500' },
  expired: { label: 'Scaduto', cls: 'bg-red-100 text-red-600' },
};
const RULE_LABELS = { full: null, monthly_discount_20: 'sconto mensile −20%', mid_term: 'listino medio termine' };

function ArchiveTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/quotes').then(res => setData(res.data))
      .catch(() => toast.error('Errore caricamento archivio'))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/quotes/${id}/status`, { status });
      toast.success('Stato aggiornato');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };

  const cancelBooking = async (bookingCode) => {
    if (!window.confirm(`Annullare la prenotazione ${bookingCode}? Gli addebiti automatici programmati verranno cancellati. L'eventuale rimborso va fatto manualmente da PayPal.`)) return;
    try {
      const res = await api.post(`/quotes/bookings/${encodeURIComponent(bookingCode)}/cancel`);
      toast.success(res.data.message);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore annullamento'); }
  };

  const resendEmail = async (q) => {
    if (!window.confirm(`Reinviare l'email al cliente ${q.client.email}?`)) return;
    try {
      const res = await api.post(`/quotes/${q.id}/resend-email`);
      toast.success(res.data.message);
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore reinvio email'); }
  };

  if (loading) return <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" /></div>;
  const stats = data?.stats || {};
  return (
    <div className="space-y-4" data-testid="quotes-archive">
      <div className="flex flex-wrap gap-2 text-xs">
        {Object.entries(STATUS_META).map(([k, m]) => (
          <span key={k} className={`px-2.5 py-1 rounded-full font-medium ${m.cls}`}>{m.label}: {stats[k] || 0}</span>
        ))}
        <span className="px-2.5 py-1 rounded-full bg-slate-800 text-white font-medium" data-testid="quotes-conversion">Conversione: {data?.conversion_pct || 0}%</span>
        <Button size="sm" variant="ghost" onClick={load}><RefreshCw className="h-3.5 w-3.5" /></Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm whitespace-nowrap">
          <thead><tr className="text-xs text-slate-500 border-b">
            <th className="text-left py-2">Data</th><th className="text-left py-2">Cliente</th>
            <th className="text-left py-2">Soggiorno</th><th className="text-left py-2">Immobili</th>
            <th className="text-left py-2">Stato</th><th className="text-left py-2">Pagamento</th><th className="text-left py-2">Pagina</th><th className="text-left py-2">Azioni</th>
          </tr></thead>
          <tbody>
            {(data?.quotes || []).map(q => (
              <tr key={q.id} className="border-b last:border-0 hover:bg-slate-50" data-testid={`quote-row-${q.id}`}>
                <td className="py-2 text-xs text-slate-500">{new Date(q.created_at).toLocaleDateString('it-IT')}</td>
                <td className="py-2"><span className="font-medium">{q.client.first_name} {q.client.last_name}</span><br /><span className="text-xs text-slate-400">{q.client.email}</span></td>
                <td className="py-2 text-xs">{q.check_in} → {q.check_out}<br /><span className="text-slate-400">{q.guests} ospiti{q.children_under_10 ? ` (👶${q.children_under_10} <10)` : ''}{q.pets ? ' · 🐾' : ''}{q.travel_reason ? ` · ${TRAVEL_LABELS[q.travel_reason] || q.travel_reason}` : ''}</span></td>
                <td className="py-2 text-xs max-w-[220px] truncate">{q.snapshots.map(s => s.name).join(', ')}</td>
                <td className="py-2"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_META[q.status]?.cls || ''}`}>{STATUS_META[q.status]?.label || q.status}</span></td>
                <td className="py-2 text-xs" data-testid={`quote-payment-${q.id}`}>
                  {q.payment ? (
                    <div>
                      <span className="text-emerald-700 font-medium">💳 {q.payment.paid_now > 0 ? `${eur(q.payment.paid_now)} incassato` : 'Metodo salvato (0 € oggi)'}</span>
                      {q.payment.booking_code && <><br /><span className="text-slate-400">{q.payment.booking_code}</span></>}
                      {q.scheduled_charge && (
                        <><br />
                          <span className={q.scheduled_charge.status === 'failed' ? 'text-red-600 font-bold' : q.scheduled_charge.status === 'done' ? 'text-emerald-600' : 'text-slate-500'}>
                            {q.scheduled_charge.status === 'failed' ? '⚠️ ADDEBITO FALLITO' : q.scheduled_charge.status === 'done' ? 'Saldo incassato ✓' : `Saldo ${eur(q.scheduled_charge.amount)} il ${new Date(q.scheduled_charge.charge_date).toLocaleDateString('it-IT')}${q.scheduled_charge.balance_mode === 'payin3' ? ' · 3 rate' : ''}`}
                          </span>
                        </>
                      )}
                    </div>
                  ) : <span className="text-slate-300">—</span>}
                </td>
                <td className="py-2"><a href={`/preventivo/${q.token}`} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline text-xs flex items-center gap-1">Apri <ExternalLink className="h-3 w-3" /></a></td>
                <td className="py-2">
                  <div className="flex items-center gap-1">
                    <button onClick={() => resendEmail(q)} title="Reinvia email al cliente (info + link)" className="p-1 rounded hover:bg-blue-50 text-blue-600" data-testid={`quote-resend-email-${q.id}`}><Send className="h-4 w-4" /></button>
                    {!['booked', 'lost'].includes(q.status) && (
                      <>
                        <button onClick={() => setStatus(q.id, 'booked')} title="Segna prenotato" className="p-1 rounded hover:bg-emerald-50 text-emerald-600" data-testid={`quote-mark-booked-${q.id}`}><CheckCircle2 className="h-4 w-4" /></button>
                        <button onClick={() => setStatus(q.id, 'lost')} title="Segna perso" className="p-1 rounded hover:bg-red-50 text-red-500" data-testid={`quote-mark-lost-${q.id}`}><XCircle className="h-4 w-4" /></button>
                      </>
                    )}
                    {q.status === 'booked' && q.payment?.booking_code && (
                      <button onClick={() => cancelBooking(q.payment.booking_code)} title="Annulla prenotazione (storno)" className="text-[10px] text-red-500 underline hover:text-red-700" data-testid={`quote-cancel-booking-${q.id}`}>Annulla pren.</button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {(data?.quotes || []).length === 0 && <tr><td colSpan={8} className="py-8 text-center text-slate-400 text-sm">Nessun preventivo ancora inviato</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const TRAVEL_LABELS = { leisure: 'Svago/Vacanza', work: 'Lavoro/Trasferta', study: 'Studio', health: 'Cure mediche', other: 'Altro' };
const PETS_BADGE = {
  allowed: { label: '🐾 animali ammessi', cls: 'bg-emerald-100 text-emerald-700' },
  on_request: { label: '🐾 su richiesta', cls: 'bg-amber-100 text-amber-800' },
  unknown: { label: '🐾 da verificare', cls: 'bg-yellow-100 text-yellow-800' },
};

export default function PreventiviPage() {
  const [tab, setTab] = useState('new');
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '', check_in: '', check_out: '', guests: 2, zone_query: '', children_under_10: 0, pets: false, travel_reason: 'leisure' });
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null);
  const [selected, setSelected] = useState([]);
  const [frequency, setFrequency] = useState('');
  const [sending, setSending] = useState(false);

  const f = (k, v) => setForm(s => ({ ...s, [k]: v }));
  const nights = form.check_in && form.check_out ? Math.round((new Date(form.check_out) - new Date(form.check_in)) / 86400000) : 0;

  const search = async () => {
    if (!form.check_in || !form.check_out) return toast.error('Inserisci le date del soggiorno');
    setSearching(true); setResults(null);
    try {
      const res = await api.post('/quotes/search', { check_in: form.check_in, check_out: form.check_out, guests: Number(form.guests) || 2, zone_query: form.zone_query, children_under_10: Number(form.children_under_10) || 0, pets: !!form.pets });
      setResults(res.data);
      setSelected(res.data.suggested || []);
      if (res.data.zone_note) toast.info(res.data.zone_note);
      if (!res.data.count) toast.warning(res.data.empty_reason || 'Nessun immobile disponibile per zona/date richieste', { duration: 15000 });
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore ricerca'); }
    finally { setSearching(false); }
  };

  const toggle = (pid) => setSelected(sel =>
    sel.includes(pid) ? sel.filter(x => x !== pid) : (sel.length >= 3 ? (toast.warning('Massimo 3 immobili per preventivo'), sel) : [...sel, pid]));

  const send = async () => {
    if (!form.first_name || !form.last_name || !form.email) return toast.error('Compila nome, cognome ed email del cliente');
    if (!selected.length) return toast.error('Seleziona almeno un immobile');
    setSending(true);
    try {
      const res = await api.post('/quotes', {
        client: { first_name: form.first_name, last_name: form.last_name, email: form.email, phone: form.phone },
        check_in: form.check_in, check_out: form.check_out, guests: Number(form.guests) || 2,
        zone_query: form.zone_query, property_ids: selected,
        children_under_10: Number(form.children_under_10) || 0, pets: !!form.pets, travel_reason: form.travel_reason || null,
        top_property_id: results?.top_property_id && selected.includes(results.top_property_id) ? results.top_property_id : null,
        zone_fallback: !!results?.nearby_fallback,
        periodic_frequency: nights >= 28 && frequency ? frequency : null,
      });
      toast.success(res.data.message);
      window.open(`/preventivo/${res.data.quote.token}`, '_blank');
      setResults(null); setSelected([]);
      setForm({ first_name: '', last_name: '', email: '', phone: '', check_in: '', check_out: '', guests: 2, zone_query: '', children_under_10: 0, pets: false, travel_reason: 'leisure' });
      setTab('archive');
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore invio preventivo'); }
    finally { setSending(false); }
  };

  const inputCls = 'w-full px-3 py-2 border rounded-lg text-sm';
  return (
    <div className="p-6 max-w-6xl mx-auto space-y-4" data-testid="quotes-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-bold text-slate-800">Preventivi</h1>
          <p className="text-sm text-slate-500">Cerca disponibilità, invia il preventivo al cliente e monitora le conversioni</p>
        </div>
        <div className="flex bg-slate-100 p-1 rounded-lg">
          <Button variant={tab === 'new' ? 'default' : 'ghost'} size="sm" onClick={() => setTab('new')} data-testid="quotes-tab-new">Nuovo preventivo</Button>
          <Button variant={tab === 'archive' ? 'default' : 'ghost'} size="sm" onClick={() => setTab('archive')} data-testid="quotes-tab-archive">Archivio</Button>
          <Button variant={tab === 'links' ? 'default' : 'ghost'} size="sm" onClick={() => setTab('links')} data-testid="quotes-tab-links">Link partner</Button>
        </div>
      </div>

      {tab === 'links' ? <Card className="p-5"><PartnerLinksTab /></Card> : tab === 'archive' ? <Card className="p-5"><ArchiveTab /></Card> : (
        <>
          <Card className="p-5 space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div><label className="text-xs font-medium text-slate-600">Nome *</label><input className={inputCls} value={form.first_name} onChange={e => f('first_name', e.target.value)} data-testid="quote-first-name" /></div>
              <div><label className="text-xs font-medium text-slate-600">Cognome *</label><input className={inputCls} value={form.last_name} onChange={e => f('last_name', e.target.value)} data-testid="quote-last-name" /></div>
              <div><label className="text-xs font-medium text-slate-600">Email *</label><input type="email" className={inputCls} value={form.email} onChange={e => f('email', e.target.value)} data-testid="quote-email" /></div>
              <div><label className="text-xs font-medium text-slate-600">Telefono</label><input className={inputCls} value={form.phone} onChange={e => f('phone', e.target.value)} data-testid="quote-phone" /></div>
              <div><label className="text-xs font-medium text-slate-600">Check-in *</label><input type="date" className={inputCls} value={form.check_in} onChange={e => f('check_in', e.target.value)} data-testid="quote-check-in" /></div>
              <div><label className="text-xs font-medium text-slate-600">Check-out *</label><input type="date" className={inputCls} value={form.check_out} onChange={e => f('check_out', e.target.value)} data-testid="quote-check-out" /></div>
              <div><label className="text-xs font-medium text-slate-600">Ospiti</label><input type="number" min="1" className={inputCls} value={form.guests} onChange={e => f('guests', e.target.value)} data-testid="quote-guests" /></div>
              <div><label className="text-xs font-medium text-slate-600">di cui bimbi &lt;10 anni (soggiorno gratis)</label><input type="number" min="0" className={inputCls} value={form.children_under_10} onChange={e => f('children_under_10', e.target.value)} data-testid="quote-children" /></div>
              <div><label className="text-xs font-medium text-slate-600">Motivo del viaggio</label>
                <select className={inputCls + ' bg-white'} value={form.travel_reason} onChange={e => f('travel_reason', e.target.value)} data-testid="quote-travel-reason">
                  {Object.entries(TRAVEL_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div className="flex items-end pb-1"><label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer"><input type="checkbox" checked={form.pets} onChange={e => f('pets', e.target.checked)} data-testid="quote-pets" /> 🐾 Viaggia con animali</label></div>
              <div><label className="text-xs font-medium text-slate-600">Zona / immobile (ricerca libera)</label><input className={inputCls} placeholder='es. "Chiavari", "riviera di levante"...' value={form.zone_query} onChange={e => f('zone_query', e.target.value)} data-testid="quote-zone" /></div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <Button onClick={search} disabled={searching} data-testid="quote-search-btn">
                {searching ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Search className="h-4 w-4 mr-1" />} Cerca disponibilità
              </Button>
              {nights > 0 && <span className="text-xs text-slate-500">{nights} notti{nights >= 30 && nights < 60 ? ' · sconto mensile −20% automatico' : ''}{nights >= 60 ? ' · medio termine' : ''}</span>}
              {nights >= 28 && (
                <select value={frequency} onChange={e => setFrequency(e.target.value)} className="px-2 py-1.5 border rounded-lg text-sm bg-white" data-testid="quote-frequency">
                  <option value="">Pulizie periodiche: nessuna</option>
                  <option value="weekly">Pulizia settimanale + biancheria</option>
                  <option value="biweekly">Pulizia bisettimanale + biancheria</option>
                  <option value="monthly">Pulizia mensile + biancheria</option>
                </select>
              )}
            </div>
          </Card>

          {results && (
            <Card className="p-5 space-y-3" data-testid="quote-results">
              {results.nearby_fallback && (
                <div className="bg-amber-50 border border-amber-300 text-amber-800 text-sm rounded-lg p-3" data-testid="quote-nearby-banner">
                  ⚠️ Nessun immobile disponibile nella zona cercata: ti mostriamo <b>i più vicini</b>. Nell'email il cliente verrà avvisato che sono le migliori alternative in zona.
                </div>
              )}
              <div className="flex items-center justify-between flex-wrap gap-2">
                <p className="text-sm font-semibold text-slate-700">{results.count} immobili disponibili {results.ai_used && <span className="text-xs text-indigo-500">(zona interpretata dall'AI)</span>}</p>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500">{selected.length}/3 selezionati</span>
                  <Button onClick={send} disabled={sending || !selected.length} data-testid="quote-send-btn">
                    {sending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />} Invia preventivo
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {results.results.map(r => {
                  const isSel = selected.includes(r.property_id);
                  const isTop = r.property_id === results.top_property_id;
                  const isPush = results.suggested.includes(r.property_id) && !isTop;
                  return (
                    <button key={r.property_id} onClick={() => toggle(r.property_id)}
                      className={`text-left border-2 rounded-xl overflow-hidden transition-colors ${isSel ? 'border-blue-500 shadow-md' : 'border-transparent hover:border-slate-200'}`}
                      data-testid={`quote-result-${r.property_code}`}>
                      <div className="relative h-36 bg-slate-100">
                        {r.cover_url && <img src={r.cover_url} alt={r.name} className="w-full h-full object-cover" />}
                        {isTop && <span className="absolute top-2 left-2 bg-amber-500 text-white text-[11px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1"><Star className="h-3 w-3" /> TOP</span>}
                        {isPush && <span className="absolute top-2 left-2 bg-violet-500 text-white text-[11px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1"><TrendingDown className="h-3 w-3" /> Da spingere</span>}
                        {isSel && <span className="absolute top-2 right-2 bg-blue-600 text-white text-[11px] font-bold px-2 py-0.5 rounded-full">✓ Selezionato</span>}
                      </div>
                      <div className="p-3 bg-white">
                        <p className="font-semibold text-sm truncate">{r.name}</p>
                        <p className="text-xs text-slate-400">{r.city} · {r.bookings_12m} pren. 12m {r.min_stay_warning && <span className="text-red-500">· min stay!</span>}</p>
                        {r.pets_status && PETS_BADGE[r.pets_status] && (
                          <span className={`inline-block mt-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full ${PETS_BADGE[r.pets_status].cls}`} data-testid={`pets-badge-${r.property_code}`}>{PETS_BADGE[r.pets_status].label}</span>
                        )}
                        <p className="mt-1 font-bold text-blue-700">{r.monthly_total ? `${eur(r.monthly_total)}/mese` : eur(r.total)}</p>
                        {r.monthly_total && <p className="text-[11px] text-slate-500">Totale {r.accommodation.nights} notti: {eur(r.total)}</p>}
                        <p className="text-[11px] text-slate-400">
                          Soggiorno {eur(r.accommodation.accommodation_total)}{RULE_LABELS[r.accommodation.pricing_rule] ? ` (${RULE_LABELS[r.accommodation.pricing_rule]})` : ''} + pulizia {eur(r.fees.cleaning)} + biancheria {eur(r.fees.linen)}
                        </p>
                        {r.tourist_tax && <p className="text-[11px] text-amber-700">+ tassa soggiorno {eur(r.tourist_tax.amount)} in loco</p>}
                      </div>
                    </button>
                  );
                })}
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
